import { appRoutes } from '../routes';
import type { IUserPermission } from '../types';

/**
 * `routeName` is the contract between the email templates (see
 * `docs/deep-links.md`), the mobile app and the web app.
 *
 * It exists because the path alone is ambiguous: five of the CTAs point at
 * plain `/orth` and only `routeName` says whether that means "open the chat",
 * "open the farm profile", "start adding a plot", "add a note" or "create an
 * action". Mobile reads it off the universal link; the web reads it here.
 */
export const DEEP_LINK_ROUTE_NAMES = [
  'orth_chat',
  'plots_profile',
  'create_plot_map',
  'field_open_notes',
  'create_action_farm',
  'subscription',
  'settings',
  'personalize',
] as const;

export type DeepLinkRouteName = (typeof DEEP_LINK_ROUTE_NAMES)[number];

export const isDeepLinkRouteName = (
  value: string | null | undefined
): value is DeepLinkRouteName =>
  !!value && (DEEP_LINK_ROUTE_NAMES as readonly string[]).includes(value);

/**
 * Where each CTA belongs on the web.
 *
 * Needed because the path does not survive a logged-out click: the auth
 * middleware forwards the query string to `/auth/login`, but login always ends
 * on `/orth`, so `subscription` and `settings` would otherwise be swallowed.
 * Re-deriving the path from `routeName` puts the user back where the CTA meant.
 */
export const DEEP_LINK_PATH: Record<DeepLinkRouteName, string> = {
  orth_chat: appRoutes.orth,
  plots_profile: appRoutes.orth,
  create_plot_map: appRoutes.orth,
  field_open_notes: appRoutes.orth,
  create_action_farm: appRoutes.orth,
  subscription: appRoutes.subscription,
  settings: `${appRoutes.setting}?tab=preferences`,
  personalize: appRoutes.personalize,
};

/**
 * A CTA the user has no permission for is dropped rather than opening a panel
 * or modal the API would reject. Same gates the `+` menu uses (`CreateBtn`).
 */
export const DEEP_LINK_PERMISSIONS = [
  'create-property-field',
  'upload-property-attachment',
  'create-property-action',
  'read-orth-skills',
  'read-knowledge-documents',
] as const satisfies readonly IUserPermission[];

export type DeepLinkPermission = (typeof DEEP_LINK_PERMISSIONS)[number];

export const DEEP_LINK_PERMISSION: Partial<
  Record<DeepLinkRouteName, DeepLinkPermission>
> = {
  create_plot_map: 'create-property-field',
  field_open_notes: 'upload-property-attachment',
  create_action_farm: 'create-property-action',
};

/** CTAs that need any one of the listed permissions (see auth middleware). */
export const DEEP_LINK_PERMISSION_ANY: Partial<
  Record<DeepLinkRouteName, readonly DeepLinkPermission[]>
> = {
  personalize: ['read-orth-skills', 'read-knowledge-documents'],
};

/**
 * Carries a deep link across the login redirect, which drops the query string:
 * the auth middleware forwards it to `/auth/login`, but login then navigates to
 * `/orth` on its own.
 */
export const PENDING_DEEP_LINK_SESSION_KEY = 'pendingDeepLink';

export type PendingDeepLink = {
  routeName: DeepLinkRouteName;
  accountId?: string | null;
  propertyId?: string | null;
  /** `orth_chat` only: the thread to reopen, and where to scroll inside it. */
  threadId?: string | null;
  messageId?: string | null;
  fromMentionNotification?: boolean;
};

/** Query params a deep link owns; stripped once it has been applied. */
export const DEEP_LINK_PARAMS = [
  'routeName',
  'src',
  'accountId',
  'propertyId',
  'threadId',
  'messageId',
  'fromMentionNotification',
];

/**
 * Reads a deep link out of a query string.
 *
 * Chat links predate `routeName` and are still sent as a bare `threadId`, so a
 * thread with no route name is read as `orth_chat`.
 */
export const readDeepLinkParams = (
  params: Pick<URLSearchParams, 'get'>
): PendingDeepLink | null => {
  const routeNameParam = params.get('routeName');
  const threadId = params.get('threadId');
  const routeName: DeepLinkRouteName | null = isDeepLinkRouteName(routeNameParam)
    ? routeNameParam
    : threadId
      ? 'orth_chat'
      : null;
  if (!routeName) return null;

  return {
    routeName,
    accountId: params.get('accountId'),
    propertyId: params.get('propertyId'),
    threadId,
    messageId: params.get('messageId'),
    fromMentionNotification: params.get('fromMentionNotification') === 'true',
  };
};

export const readPendingDeepLink = (): PendingDeepLink | null => {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_DEEP_LINK_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingDeepLink;
    return isDeepLinkRouteName(parsed?.routeName) ? parsed : null;
  } catch {
    return null;
  }
};

export const writePendingDeepLink = (pending: PendingDeepLink) => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(
    PENDING_DEEP_LINK_SESSION_KEY,
    JSON.stringify(pending)
  );
};

export const clearPendingDeepLink = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PENDING_DEEP_LINK_SESSION_KEY);
};
